import React, { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import classNames from 'classnames';
import styles from './Persona.module.css';
import type { PersonaFormState, PersonaFormType } from '.';
import { CtaButton } from 'admin/shared-components/CtaButton/CtaButton';
import Modal from 'admin/shared-components/Modal/Modal';
import { Table } from 'admin/shared-components/Table/Table';
import { ActionBar } from 'admin/shared-components/ActionBar/ActionBar';
import { PersonaSearchResponse, PersonaRowData, PersonaToast } from 'admin/types';
import { mobxInjectSelect } from 'common/utils';
import { useAppSelector } from 'redux-stores';

type PersonaTableProps = {
  showPersonaForm: boolean;
  setPersonaFormState: React.Dispatch<React.SetStateAction<PersonaFormState>>;
  setEditMode: (isEdit: boolean) => void;
  setSelectedPersona: React.Dispatch<React.SetStateAction<PersonaRowData | undefined>>;
  setToastMessageData: React.Dispatch<React.SetStateAction<PersonaToast | undefined>>;
};

type MobxProps = {
  deletePersona: (personaId: string, selectedOrganizationId: string) => Promise<void>;
  findPersonas: (params: {
    query: string;
    page: number;
    selectedOrganizationId: string;
  }) => Promise<PersonaSearchResponse>;
  allowScimAdminSetting: boolean;
};
type PersonaTableRow = {
  name: string;
  titles: string;
  description: string;
  parentPersonaId: string;
  id: string;
  rawData: PersonaRowData;
  personaType: 'TITLE' | 'GROUP';
  scrimGroups: Promise<string>[] | string;
};

function PersonaTable({
  allowScimAdminSetting,
  deletePersona,
  findPersonas,
  setEditMode,
  setPersonaFormState,
  setSelectedPersona,
  setToastMessageData,
}: PersonaTableProps & MobxProps) {
  const [rows, setRows] = useState<PersonaTableRow[]>([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [isPaginated, setIsPaginated] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [queryValue, setQueryValue] = useState('');
  const debouncedSetQueryValue = useRef(debounce(setQueryValue, 300));
  const [personaToDelete, setPersonaToDelete] = useState<PersonaTableRow | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const selectedOrganizationId = useAppSelector((state) => state.admin.selectedOrganizationId);

  const columns = [
    { field: 'name', headerName: 'Persona' },
    { field: 'titles', headerName: 'Title(s)' },
    { field: 'scrimGroups', headerName: 'SCIM Group(s)' },
    { field: 'description', headerName: 'Description' },
    // Commenting out for now until we have a need for this.
    // { field: 'parentPersonaId', headerName: 'Parent' },
  ];

  useEffect(() => {
    debouncedSetQueryValue.current(searchInputValue);
  }, [searchInputValue]);

  const setRowData = useCallback(
    ({ personaList, rows }: { personaList: PersonaSearchResponse; rows: PersonaTableRow[] }) => {
      const rowsSnapshot = [...rows] as PersonaTableRow[];
      if (personaList.pageNumber < personaList.totalPages) {
        setIsPaginated(true);
        setCurrentPageNumber(personaList.pageNumber + 1);
      } else {
        setIsPaginated(false);
      }
      personaList.data.forEach((rawData: PersonaRowData) => {
        const { personaId, name, description, parentPersonaId, titles, groupDisplayNames } =
          rawData;
        rowsSnapshot.push({
          id: personaId,
          name,
          description: description || 'N/A',
          parentPersonaId: parentPersonaId || 'N/A',
          titles: titles && titles.length > 0 ? titles.join(', ') : 'N/A',
          scrimGroups: groupDisplayNames?.length > 0 ? groupDisplayNames.join(', ') : 'N/A',
          rawData,
          personaType: rawData.personaType,
        });
      });
      setRows(rowsSnapshot);
    },
    [setCurrentPageNumber, setRows]
  );

  const getTableData = useCallback(
    async ({
      rows,
      page = 1,
      currentQueryValue = '',
    }: {
      rows: PersonaTableRow[];
      page?: number;
      currentQueryValue?: string;
    }) => {
      setIsLoading(true);
      const response = await findPersonas({
        page,
        query: currentQueryValue,
        selectedOrganizationId,
      });
      setRowData({ personaList: response, rows });
      setIsLoading(false);
    },
    [findPersonas, setRowData, selectedOrganizationId]
  );

  const openForm = (row: PersonaTableRow, editMode = true) => {
    setSelectedPersona(row.rawData);
    setEditMode(editMode);
    setPersonaFormState((currentState: PersonaFormState) => ({
      ...currentState,
      display: true,
      type: row.personaType ? (row.personaType === 'TITLE' ? 'title' : 'scim') : 'title',
    }));
  };

  const onCreate = (formType: PersonaFormType) => {
    setSelectedPersona(undefined);
    setEditMode(true);
    setPersonaFormState({ display: true, type: formType });
  };

  const deletePersonaEntity = useCallback(async () => {
    if (!personaToDelete) return;
    try {
      await deletePersona(personaToDelete.id, selectedOrganizationId);
      getTableData({ rows: [] });
      setPersonaToDelete(undefined);
      setToastMessageData({ type: 'SUCCESS', message: 'Persona Deleted' });
    } catch (e) {
      setPersonaToDelete(undefined);
      setToastMessageData({ type: 'FAILURE', message: 'An Error Occurred' });
    }
  }, [deletePersona, getTableData, personaToDelete, selectedOrganizationId, setToastMessageData]);

  useEffect(() => {
    getTableData({ rows: [] });
  }, [getTableData]);

  useEffect(() => {
    getTableData({ rows: [], currentQueryValue: queryValue });
  }, [getTableData, queryValue]);

  const actions = [
    { name: 'Edit', type: 'edit' },
    { name: 'Delete', type: 'delete' },
  ];

  return (
    <div className={classNames(styles.personaTable)}>
      <ActionBar
        hasSearch={true}
        onInputChange={(e) => setSearchInputValue(e.target.value)}
        searchValue={searchInputValue}
        dataTestId={'personaSearch'}
        handleClearClick={() => {
          getTableData({ rows: [] });
          setSearchInputValue('');
        }}
      />
      <div className={classNames(styles.buttonsBar)}>
        <div className={classNames(styles.createButton)}>
          <CtaButton
            primary
            label={'Create a Title-based Persona'}
            onClick={() => onCreate('title')}
          />
        </div>
        {allowScimAdminSetting && (
          <div className={classNames(styles.createButton)}>
            <CtaButton
              primary
              label={'Create a SCIM-based Persona'}
              onClick={() => onCreate('scim')}
            />
          </div>
        )}
      </div>
      <Table
        actionsOrder={actions}
        columns={columns}
        emptyMessage={`No Personas${queryValue ? ` found for '${queryValue}'.` : '.'}`}
        isLoading={isLoading}
        onRowClick={(row) => openForm(row, false)}
        onRowEdit={(row) => openForm(row)}
        onRowDelete={setPersonaToDelete}
        rows={rows}
        onPaginate={() =>
          getTableData({ page: currentPageNumber, rows, currentQueryValue: queryValue })
        }
        paginated={isPaginated}
      />
      <Modal
        isOpen={!!personaToDelete}
        closeButton="Cancel"
        onSuccess={deletePersonaEntity}
        onClose={() => setPersonaToDelete(undefined)}
        canClose
        header={`Confirm Delete`}
        doneButton="Delete Persona"
        isWarningButtonPresent={true}
        modalSize="small"
        body={`Are you sure you want to delete the "${personaToDelete?.name}" Persona?`}
      />
    </div>
  );
}

export default mobxInjectSelect<PersonaTableProps, MobxProps>({
  personaStore: ['deletePersona', 'findPersonas'],
  messengerStore: ['allowScimAdminSetting'],
})(PersonaTable);
